import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash, faEnvelope, faLock, faUser, faBuilding, faPhone, faSearch, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { AuroraBackground } from '../ui/aurora-background';
import { useAuth } from '../../context/AuthContext';
import { BusinessType } from '../../types/models';
import { trackFormSubmit, trackSignUp } from '../../utils/analytics';
import SEO from '../SEO';
import { seoConfig } from '../../utils/seoData';
import { useTranslation } from 'react-i18next';
import { sortedCountryCodes, CountryCode } from '../../utils/countryCodes';

const Register: React.FC = () => {
  const navigate = useNavigate();
  const { lang } = useParams<{ lang: string }>();
  const { i18n } = useTranslation();
  // @ts-ignore - react-i18next supports namespace parameter but TypeScript types don't reflect it
  const { t: tCommon } = useTranslation('common');
  const { register, loading } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    category: BusinessType.Other,
    website: '',
    description: '',
    location: {
      address: '',
      city: '',
      country: ''
    }
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{
    name?: string;
    email?: string;
    password?: string;
    confirmPassword?: string;
    category?: string;
    description?: string;
    website?: string;
    phone?: string;
  }>({});
  const [categoryOpen, setCategoryOpen] = useState(false);
  const categoryRef = useRef<HTMLDivElement | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<CountryCode>(sortedCountryCodes.find(c => c.code === 'TR') || sortedCountryCodes[0]);
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const [countrySearchQuery, setCountrySearchQuery] = useState('');
  const countryRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (categoryRef.current && !categoryRef.current.contains(event.target as Node)) {
        setCategoryOpen(false);
      }
      if (countryRef.current && !countryRef.current.contains(event.target as Node)) {
        setCountryDropdownOpen(false);
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setCategoryOpen(false);
        setCountryDropdownOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  // Filter countries based on search query
  const filteredCountries = useMemo(() => {
    if (!countrySearchQuery.trim()) {
      return sortedCountryCodes;
    }
    const query = countrySearchQuery.toLowerCase();
    return sortedCountryCodes.filter(country => 
      country.name.toLowerCase().includes(query) ||
      country.nameEn.toLowerCase().includes(query) ||
      country.code.toLowerCase().includes(query) ||
      country.dialCode.includes(query)
    );
  }, [countrySearchQuery]);

  // Validation functions
  const validateName = (name: string): string | undefined => {
    if (!name || name.trim().length === 0) {
      return 'İşletme adı gereklidir';
    }
    if (name.trim().length > 100) {
      return 'İşletme adı 100 karakterden uzun olamaz';
    }
    return undefined;
  };

  const validateEmail = (email: string): string | undefined => {
    if (!email || email.trim().length === 0) {
      return 'E-posta adresi gereklidir';
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return 'Geçerli bir e-posta adresi giriniz';
    }
    return undefined;
  };

  const validatePassword = (password: string): string | undefined => {
    if (!password || password.length === 0) {
      return 'Şifre gereklidir';
    }
    if (password.length < 6) {
      return 'Şifre en az 6 karakter olmalıdır';
    }
    return undefined;
  };

  const validateDescription = (description: string): string | undefined => {
    if (description && description.length > 500) {
      return 'Açıklama 500 karakterden uzun olamaz';
    }
    return undefined;
  };

  const validatePhone = (phone: string): string | undefined => {
    if (phone && phone.trim().length > 0) {
      // Remove spaces and special characters for validation
      const cleanedPhone = phone.replace(/\s+/g, '').replace(/[-\/\(\)]/g, '');
      // Check if phone contains only digits
      if (!/^\d+$/.test(cleanedPhone)) {
        return 'Telefon numarası sadece rakamlardan oluşmalıdır';
      }
      // Check minimum length (at least 7 digits, max 15 digits for international format)
      if (cleanedPhone.length < 7) {
        return 'Telefon numarası en az 7 haneli olmalıdır';
      }
      if (cleanedPhone.length > 15) {
        return 'Telefon numarası en fazla 15 haneli olabilir';
      }
    }
    return undefined;
  };

  const validateWebsite = (website: string): string | undefined => {
    if (website && website.trim().length > 0) {
      const trimmedWebsite = website.trim();
      // Check if it starts with http:// or https://
      if (!trimmedWebsite.startsWith('http://') && !trimmedWebsite.startsWith('https://')) {
        return 'URL http:// veya https:// ile başlamalıdır';
      }
      try {
        new URL(trimmedWebsite);
      } catch {
        return 'Geçerli bir URL giriniz (örn: https://www.example.com)';
      }
    }
    return undefined;
  };

  const validateCategory = (category: BusinessType): string | undefined => {
    if (!Object.values(BusinessType).includes(category)) {
      return 'Geçersiz işletme türü';
    }
    return undefined;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    // Clear field error when user starts typing
    if (fieldErrors[name as keyof typeof fieldErrors]) {
      setFieldErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }

    // Real-time validation
    let error: string | undefined;
    if (name === 'name') {
      error = validateName(value);
    } else if (name === 'email') {
      error = validateEmail(value);
    } else if (name === 'password') {
      error = validatePassword(value);
      // Also validate confirmPassword if it exists
      if (formData.confirmPassword && formData.confirmPassword !== value) {
        setFieldErrors(prev => ({
          ...prev,
          confirmPassword: 'Şifreler eşleşmiyor!'
        }));
      } else if (formData.confirmPassword && formData.confirmPassword === value) {
        setFieldErrors(prev => ({
          ...prev,
          confirmPassword: undefined
        }));
      }
    } else if (name === 'confirmPassword') {
      if (value !== formData.password) {
        error = 'Şifreler eşleşmiyor!';
      }
    } else if (name === 'phone') {
      error = validatePhone(value);
    } else if (name === 'description') {
      error = validateDescription(value);
    } else if (name === 'website') {
      error = validateWebsite(value);
    }

    if (error) {
      setFieldErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
    
    if (name.startsWith('location.')) {
      const locationField = name.split('.')[1];
      setFormData({
        ...formData,
        location: {
          ...formData.location,
          [locationField]: value
        }
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});
    trackFormSubmit('register_form');

    // Validate all fields
    const errors: typeof fieldErrors = {};
    
    const nameError = validateName(formData.name);
    if (nameError) errors.name = nameError;

    const emailError = validateEmail(formData.email);
    if (emailError) errors.email = emailError;

    const passwordError = validatePassword(formData.password);
    if (passwordError) errors.password = passwordError;

    const categoryError = validateCategory(formData.category);
    if (categoryError) errors.category = categoryError;

    if (formData.phone) {
      const phoneError = validatePhone(formData.phone);
      if (phoneError) errors.phone = phoneError;
    }

    if (formData.description) {
      const descriptionError = validateDescription(formData.description);
      if (descriptionError) errors.description = descriptionError;
    }

    if (formData.website) {
      const websiteError = validateWebsite(formData.website);
      if (websiteError) errors.website = websiteError;
    }

    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Şifreler eşleşmiyor!';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    if (!acceptTerms) {
      setError('Kullanım şartlarını kabul etmelisiniz!');
      return;
    }

    try {
      const { confirmPassword, ...registerData } = formData;
      // Combine country code with phone number if phone is provided
      if (registerData.phone) {
        registerData.phone = `${selectedCountry.dialCode} ${registerData.phone}`.trim();
      }
      // Add current language to preferences
      const currentLang = lang || i18n.language || 'tr';
      (registerData as any).preferences = {
        lang: currentLang === 'tr' || currentLang === 'en' ? currentLang : 'tr'
      };
      await register(registerData);
      trackSignUp('email');
      // Navigate to email verification page with email
      navigate(`/${currentLang}/verify-email`, { state: { email: formData.email } });
    } catch (err: any) {
      // Handle server validation errors
      if (err.response?.data?.errors) {
        const serverErrors = err.response.data.errors;
        const fieldErrorMap: typeof fieldErrors = {};
        
        if (serverErrors.name) fieldErrorMap.name = serverErrors.name;
        if (serverErrors.email) fieldErrorMap.email = serverErrors.email;
        if (serverErrors.password) fieldErrorMap.password = serverErrors.password;
        if (serverErrors.category) fieldErrorMap.category = serverErrors.category;
        if (serverErrors.description) fieldErrorMap.description = serverErrors.description;
        if (serverErrors.phone) fieldErrorMap.phone = serverErrors.phone;
        if (serverErrors.googleBusiness?.mapsUrl) fieldErrorMap.website = serverErrors.googleBusiness.mapsUrl;
        
        if (Object.keys(fieldErrorMap).length > 0) {
          setFieldErrors(fieldErrorMap);
        } else {
          setError(err.response.data.message || err.message || 'Kayıt olurken bir hata oluştu');
        }
      } else {
        setError(err.message || 'Kayıt olurken bir hata oluştu');
      }
    }
  };

  return (
    <>
      <SEO {...seoConfig.register} />
      <AuroraBackground className="w-full h-full overflow-scroll">
      <div className="w-full min-h-screen flex items-center justify-center relative overflow-hidden py-10">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-purple-900/20 to-gray-900"></div>
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}></div>

        {/* Floating Elements */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-purple-500/10 rounded-full blur-xl animate-float"></div>
        <div className="absolute top-40 right-20 w-32 h-32 bg-blue-500/10 rounded-full blur-xl animate-float" style={{ animationDelay: '1s' }}></div>
        <div className="absolute bottom-20 left-1/4 w-24 h-24 bg-pink-500/10 rounded-full blur-xl animate-float" style={{ animationDelay: '2s' }}></div>

        <div className="relative z-10 w-full max-w-md mx-auto px-6">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center mb-6 cursor-pointer" onClick={() => navigate('/')}>
              <img 
                src="/logo.svg" 
                alt="Winnomi Logo" 
                className="w-16 h-16 object-contain"
              />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Hesap Oluşturun</h2>
            <p className="text-white/70">Sosyal medya analitiğinize başlayın</p>
          </div>

          {/* Register Form */}
          <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-3xl p-8 shadow-2xl">
            {error && (
              <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
                <p className="text-red-400 text-sm text-center">{error}</p>
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Business Name */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-white/80 mb-2">
                  İşletme Adı
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FontAwesomeIcon icon={faBuilding} className="text-white/40 text-sm" />
                  </div>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className={`w-full pl-10 pr-4 py-3 bg-white/5 border rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300 ${
                      fieldErrors.name ? 'border-red-500/50 focus:ring-red-500' : 'border-white/10'
                    }`}
                    placeholder="İşletme adınız"
                    required
                  />
                </div>
                {fieldErrors.name && (
                  <p className="mt-1 text-xs text-red-400">{fieldErrors.name}</p>
                )}
              </div>

              {/* Email Field */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-white/80 mb-2">
                  E-posta Adresi
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FontAwesomeIcon icon={faEnvelope} className="text-white/40 text-sm" />
                  </div>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`w-full pl-10 pr-4 py-3 bg-white/5 border rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300 ${
                      fieldErrors.email ? 'border-red-500/50 focus:ring-red-500' : 'border-white/10'
                    }`}
                    placeholder="ornek@email.com"
                    required
                  />
                </div>
                {fieldErrors.email && (
                  <p className="mt-1 text-xs text-red-400">{fieldErrors.email}</p>
                )}
              </div>

              {/* Business Category */}
              <div>
                <label htmlFor="category" className="block text-sm font-medium text-white/80 mb-2">
                  İşletme Kategorisi
                </label>
                <div
                  className="relative"
                  ref={categoryRef}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <button
                    type="button"
                    aria-haspopup="listbox"
                    aria-expanded={categoryOpen}
                    onClick={() => setCategoryOpen(!categoryOpen)}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                    }}
                    className={`w-full pl-10 pr-10 py-3 bg-white/5 border rounded-xl text-left text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300 ${
                      fieldErrors.category ? 'border-red-500/50 focus:ring-red-500' : 'border-white/10'
                    }`}
                  >
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FontAwesomeIcon icon={faBuilding} className="text-white/40 text-sm" />
                  </div>
                    <span className="block truncate">
                      {(() => {
                        const labelMap: Record<string, string> = {
                          [BusinessType.Restaurant]: 'Restoran',
                          [BusinessType.Market]: 'Market',
                          [BusinessType.Cafe]: 'Kafe',
                          [BusinessType.Retail]: 'Perakende',
                          [BusinessType.Service]: 'Hizmet',
                          [BusinessType.Technology]: 'Teknoloji',
                          [BusinessType.Healthcare]: 'Sağlık',
                          [BusinessType.Education]: 'Eğitim',
                          [BusinessType.Finance]: 'Finans',
                          [BusinessType.Manufacturing]: 'Üretim',
                          [BusinessType.Other]: 'Diğer',
                        };
                        return labelMap[formData.category] || 'Kategori seçin';
                      })()}
                    </span>
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-white/50">
                      {/* Chevron */}
                      <svg className={`h-4 w-4 transform transition-transform ${categoryOpen ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" clipRule="evenodd" />
                      </svg>
                    </span>
                  </button>
                  {categoryOpen && (
                    <ul
                      role="listbox"
                      className="absolute z-20 mt-2 max-h-60 w-full overflow-auto rounded-xl bg-gray-900/90 backdrop-blur-xl border border-white/10 shadow-xl"
                    >
                      {[
                        { value: BusinessType.Restaurant, label: 'Restoran' },
                        { value: BusinessType.Market, label: 'Market' },
                        { value: BusinessType.Cafe, label: 'Kafe' },
                        { value: BusinessType.Retail, label: 'Perakende' },
                        { value: BusinessType.Service, label: 'Hizmet' },
                        { value: BusinessType.Technology, label: 'Teknoloji' },
                        { value: BusinessType.Healthcare, label: 'Sağlık' },
                        { value: BusinessType.Education, label: 'Eğitim' },
                        { value: BusinessType.Finance, label: 'Finans' },
                        { value: BusinessType.Manufacturing, label: 'Üretim' },
                        { value: BusinessType.Other, label: 'Diğer' },
                      ].map(opt => (
                        <li
                          key={opt.value}
                          role="option"
                          aria-selected={formData.category === opt.value}
                          onMouseDown={(e) => {
                            e.stopPropagation();
                            setFormData({ ...formData, category: opt.value as BusinessType });
                            setCategoryOpen(false);
                            // Clear category error when selecting
                            if (fieldErrors.category) {
                              setFieldErrors(prev => ({
                                ...prev,
                                category: undefined
                              }));
                            }
                          }}
                          className={`cursor-pointer px-3 py-2 text-sm text-white/90 hover:bg-white/10 ${
                            formData.category === opt.value ? 'bg-white/10' : ''
                          }`}
                        >
                          {opt.label}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                {fieldErrors.category && (
                  <p className="mt-1 text-xs text-red-400">{fieldErrors.category}</p>
                )}
              </div>

              {/* Phone Field */}
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-white/80 mb-2">
                  {tCommon('phone')} (İsteğe bağlı)
                </label>
                <div className="flex gap-2">
                  {/* Country Code Selector */}
                  <div className="relative" ref={countryRef}>
                    <button
                      type="button"
                      onClick={() => {
                        setCountryDropdownOpen(!countryDropdownOpen);
                        setCountrySearchQuery('');
                      }}
                      className="flex items-center gap-2 px-3 h-[50px] bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300 min-w-[100px]"
                    >
                      <span className="text-lg">{selectedCountry.flag}</span>
                      <span className="text-sm font-medium">{selectedCountry.dialCode}</span>
                      <FontAwesomeIcon icon={faChevronDown} className={`text-white/50 text-xs transition-transform ${countryDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {countryDropdownOpen && (
                      <div className="absolute z-30 mt-2 w-80 bg-gray-900/95 backdrop-blur-xl border border-white/10 rounded-xl shadow-xl overflow-hidden">
                        {/* Search Input */}
                        <div className="p-3 border-b border-white/10">
                          <div className="relative">
                            <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/40 text-sm" />
                            <input
                              type="text"
                              value={countrySearchQuery}
                              onChange={(e) => setCountrySearchQuery(e.target.value)}
                                    placeholder={tCommon('countryCodeSearchPlaceholder')}
                              className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                              autoFocus
                            />
                          </div>
                        </div>
                        {/* Country List */}
                        <div className="max-h-64 overflow-y-auto">
                          {filteredCountries.length > 0 ? (
                            filteredCountries.map((country) => (
                              <button
                                key={country.code}
                                type="button"
                                onClick={() => {
                                  setSelectedCountry(country);
                                  setCountryDropdownOpen(false);
                                  setCountrySearchQuery('');
                                }}
                                className={`w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-white/10 transition-colors ${
                                  selectedCountry.code === country.code ? 'bg-white/10' : ''
                                }`}
                              >
                                <span className="text-xl">{country.flag}</span>
                                <div className="flex-1 min-w-0">
                                  <div className="text-white text-sm font-medium truncate">{country.name}</div>
                                  <div className="text-white/60 text-xs truncate">{country.nameEn}</div>
                                </div>
                                <div className="flex items-center gap-2 text-white/70 text-sm">
                                  <span className="font-medium">{country.dialCode}</span>
                                  <span className="text-xs">({country.code})</span>
                                </div>
                              </button>
                            ))
                          ) : (
                                  <div className="px-4 py-8 text-center text-white/60 text-sm">
                                    {tCommon('countryNotFound')}
                                  </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  {/* Phone Number Input */}
                  <div className="relative flex-1">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FontAwesomeIcon icon={faPhone} className="text-white/40 text-sm" />
                    </div>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className={`w-full pl-10 pr-4 h-[50px] bg-white/5 border rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300 ${
                        fieldErrors.phone ? 'border-red-500/50 focus:ring-red-500' : 'border-white/10'
                      }`}
                      placeholder="555 123 45 67"
                    />
                  </div>
                </div>
                {fieldErrors.phone ? (
                  <p className="mt-1 text-xs text-red-400">{fieldErrors.phone}</p>
                ) : (
                  <p className="mt-1 text-xs text-white/50">
                    {tCommon('fullPhoneNumber')}: {selectedCountry.dialCode} {formData.phone || '...'}
                  </p>
                )}
              </div>

              {/* Website Field */}
              <div>
                <label htmlFor="website" className="block text-sm font-medium text-white/80 mb-2">
                  Website (İsteğe bağlı)
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FontAwesomeIcon icon={faBuilding} className="text-white/40 text-sm" />
                  </div>
                  <input
                    type="url"
                    id="website"
                    name="website"
                    value={formData.website}
                    onChange={handleInputChange}
                    className={`w-full pl-10 pr-4 py-3 bg-white/5 border rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300 ${
                      fieldErrors.website ? 'border-red-500/50 focus:ring-red-500' : 'border-white/10'
                    }`}
                    placeholder="https://www.example.com"
                  />
                </div>
                {fieldErrors.website && (
                  <p className="mt-1 text-xs text-red-400">{fieldErrors.website}</p>
                )}
              </div>

              {/* Password Field */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-white/80 mb-2">
                  Şifre
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FontAwesomeIcon icon={faLock} className="text-white/40 text-sm" />
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className={`w-full pl-10 pr-12 py-3 bg-white/5 border rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300 ${
                      fieldErrors.password ? 'border-red-500/50 focus:ring-red-500' : 'border-white/10'
                    }`}
                    placeholder="••••••••"
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-white/40 md:hover:text-white/60 transition-colors"
                  >
                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} className="text-sm" />
                  </button>
                </div>
                {fieldErrors.password ? (
                  <p className="mt-1 text-xs text-red-400">{fieldErrors.password}</p>
                ) : (
                  <p className="mt-1 text-xs text-white/50">En az 6 karakter olmalıdır</p>
                )}
              </div>

              {/* Confirm Password Field */}
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-white/80 mb-2">
                  Şifre Tekrar
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FontAwesomeIcon icon={faLock} className="text-white/40 text-sm" />
                  </div>
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    className={`w-full pl-10 pr-12 py-3 bg-white/5 border rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300 ${
                      fieldErrors.confirmPassword ? 'border-red-500/50 focus:ring-red-500' : 'border-white/10'
                    }`}
                    placeholder="••••••••"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-white/40 md:hover:text-white/60 transition-colors"
                  >
                    <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} className="text-sm" />
                  </button>
                </div>
                {fieldErrors.confirmPassword && (
                  <p className="mt-1 text-xs text-red-400">{fieldErrors.confirmPassword}</p>
                )}
              </div>

              {/* Terms and Conditions */}
              <div>
                <label className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    checked={acceptTerms}
                    onChange={(e) => setAcceptTerms(e.target.checked)}
                    className="w-4 h-4 text-purple-600 bg-white/5 border-white/20 rounded focus:ring-purple-500 focus:ring-2 mt-1"
                  />
                  <span className="text-sm text-white/70">
                    <a href="#" className="text-purple-400 md:hover:text-purple-300 transition-colors">
                      Kullanım Şartları
                    </a>
                    {' '}ve{' '}
                    <a href="#" className="text-purple-400 md:hover:text-purple-300 transition-colors">
                      Gizlilik Politikası
                    </a>
                    'nı kabul ediyorum
                  </span>
                </label>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-purple-500 to-purple-700 py-3 px-6 rounded-xl text-white font-semibold md:hover:from-purple-600 md:hover:to-purple-800 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-transparent transition-all duration-300 transform md:hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {loading ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    <span>Hesap oluşturuluyor...</span>
                  </div>
                ) : (
                  'Hesap Oluştur'
                )}
              </button>
            </form>

            {/* Divider */}
            <div className="mt-8 mb-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-white/10"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-transparent text-white/50">veya</span>
                </div>
              </div>
            </div>

            {/* Social Register */}
            <div className="space-y-3">
              <button className="w-full flex items-center justify-center space-x-3 py-3 px-6 bg-white/5 border border-white/10 rounded-xl text-white md:hover:bg-white/10 transition-all duration-300">
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                <span>Google ile kayıt ol</span>
              </button>
            </div>

            {/* Login Link */}
            <div className="mt-8 text-center">
              <p className="text-white/70">
                Zaten hesabınız var mı?{' '}
                <button
                  onClick={() => navigate('/login')}
                  className="text-purple-400 md:hover:text-purple-300 font-semibold transition-colors"
                >
                  Giriş yapın
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>
    </AuroraBackground>
    </>
  );
};

export default Register;
